import type { ApiError } from '../services/api';

/** What ChatWindow's error banner shows after a send fails. */
export interface ChatErrorBanner {
  message: string;
  code: string | null;
  action: string | null;
}

/** The server refused the send because the user is over a rate limit (a 429). */
export const isRateLimitError = (error: unknown): error is ApiError =>
  error instanceof Error && (error as ApiError).code === 'rate_limited';

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

/**
 * "Try again in 12 minutes." from Retry-After seconds. Minutes round up, so the
 * banner never says to retry before the limiter's window resets. Null when the
 * wait is unknown: the server's own message already says to wait.
 */
export const retryAfterHint = (seconds?: number): string | null => {
  if (seconds === undefined) return null;
  if (seconds <= 0) return 'You can try again now.';
  if (seconds < 60) return `Try again in ${plural(seconds, 'second')}.`;
  return `Try again in ${plural(Math.ceil(seconds / 60), 'minute')}.`;
};

/** A rate-limited send: the server's own sentence, and how long to wait. */
export const rateLimitBanner = (error: ApiError): ChatErrorBanner => ({
  message: error.message,
  code: 'RATE_LIMITED',
  action: retryAfterHint(error.retryAfterSeconds),
});

/** The banner for a failed send from the message box. */
export const sendFailureBanner = (error: unknown): ChatErrorBanner => {
  // Checked first: the upload limiter's message mentions "file uploads", which
  // the keyword checks below would call a broken file.
  if (isRateLimitError(error)) return rateLimitBanner(error);

  // Provide a more helpful error message
  const errorMsg = error instanceof Error ? error.message : 'Unknown error';
  let userMessage = 'An unexpected error occurred while processing your request.';
  let suggestedAction = 'Please try again. If the issue persists, try rephrasing your question or reducing the size of attached files.';

  // Check for specific error types
  if (errorMsg.toLowerCase().includes('network') || errorMsg.toLowerCase().includes('fetch')) {
    userMessage = 'Network connection error. Please check your internet connection.';
    suggestedAction = 'Verify your network connection and try again.';
  } else if (errorMsg.toLowerCase().includes('timeout')) {
    userMessage = 'Request timed out. The operation is taking longer than expected.';
    suggestedAction = 'Try a simpler query or wait a moment before trying again.';
  } else if (errorMsg.toLowerCase().includes('file') || errorMsg.toLowerCase().includes('upload')) {
    userMessage = 'File upload error. There may be an issue with the attached file.';
    suggestedAction = 'Check that your file is a valid PDF, CSV, XLSX, or TXT file under 10MB.';
  }

  return { message: userMessage + ' ' + errorMsg, code: null, action: suggestedAction };
};
